import { create } from 'zustand';
import { db } from './firebase';
import {
  collection,
  onSnapshot,
  writeBatch,
  doc,
  serverTimestamp,
  getDoc,
  getDocs,
  setDoc,
} from 'firebase/firestore';
import showsRaw from './shows.json';

// Shows are static metadata — ratings come from Firestore only
export const SHOWS = showsRaw;

// Pending write buffer — keyed by showId string
let pendingWrites = {};
let writeTimer = null;

async function flushToFirestore(userId) {
  if (!userId || Object.keys(pendingWrites).length === 0) return;
  const writes = { ...pendingWrites };
  pendingWrites = {};
  clearTimeout(writeTimer);
  writeTimer = null;

  const batch = writeBatch(db);
  for (const [showId, data] of Object.entries(writes)) {
    const ref = doc(db, 'users', userId, 'ratings', showId);
    batch.set(
      ref,
      {
        showId,
        rating: data.rating ?? 0,
        notes: data.notes ?? '',
        dismissed: data.dismissed ?? false,
        // Optional metadata for new suggestions
        ...(data.title ? { title: data.title } : {}),
        ...(data.year ? { year: data.year } : {}),
        ...(data.tags ? { tags: data.tags } : {}),
        ...(data.synopsis ? { synopsis: data.synopsis } : {}),
        ...(data.poster_path ? { poster_path: data.poster_path } : {}),
        lastUpdated: serverTimestamp(),
      },
      { merge: true }
    );
  }

  try {
    console.log('[Firestore] Auth UID:', userId);
    console.log('[Firestore] DB app:', db.app.options.projectId);
    console.log('[Firestore] Committing batch for', Object.keys(writes).length, 'show(s):', Object.keys(writes));
    await batch.commit();
    console.log('[Firestore] Batch commit succeeded');
  } catch (err) {
    // Re-queue failed writes so next flush retries them
    console.error('Firestore batch write failed:', err);
    Object.assign(pendingWrites, writes);
  }
}

const useStore = create((set, get) => ({
  shows: SHOWS,
  userRatings: {},
  authUser: null,
  onboardingComplete: false,
  _onboardingChecked: false,
  userPreferences: null,
  _unsubRatings: null,

  setAuthUser: (user) => {
    set({ authUser: user });
    if (user) {
      get().startRatingsListener(user.uid);
    } else {
      get().stopRatingsListener();
      set({ userRatings: {}, onboardingComplete: false, _onboardingChecked: false, userPreferences: null });
      pendingWrites = {};
    }
  },

  completeOnboarding: async (preferences = null) => {
    set({ onboardingComplete: true });
    const userId = get().authUser?.uid;
    if (!userId) return;
    try {
      const onboardingRef = doc(db, 'users', userId, 'meta', 'onboarding');
      await setDoc(onboardingRef, { complete: true, completedAt: serverTimestamp() });
      if (preferences && (preferences.tone?.length || preferences.era?.length || preferences.pacing?.length)) {
        const prefsRef = doc(db, 'users', userId, 'meta', 'preferences');
        await setDoc(prefsRef, {
          tone: preferences.tone || [],
          era: preferences.era || [],
          pacing: preferences.pacing || [],
          savedAt: serverTimestamp(),
        });
        set({ userPreferences: preferences });
      }
    } catch (err) {
      console.error('Failed to save onboarding meta:', err);
    }
  },

  // Optimistic update → buffer for batch write
  setUserRating: (showId, update) => {
    const id = String(showId);
    const existing = get().userRatings[id] ?? {};
    const merged = { ...existing, ...update };

    set(state => ({
      userRatings: { ...state.userRatings, [id]: merged },
    }));

    pendingWrites[id] = merged;
    if (writeTimer) clearTimeout(writeTimer);
    writeTimer = setTimeout(() => flushToFirestore(get().authUser?.uid), 1500);
  },

  // Force-flush pending writes (call on beforeunload)
  flushNow: () => flushToFirestore(get().authUser?.uid),

  startRatingsListener: async (userId) => {
    const prev = get()._unsubRatings;
    if (prev) prev();

    // Resolve onboarding status once before subscribing — never infer it from live snapshots.
    // This prevents the snapshot handler from kicking the user out of onboarding mid-flow
    // when their first rating write lands in Firestore.
    try {
      const [metaSnap, prefsSnap, ratingsSnap] = await Promise.all([
        getDoc(doc(db, 'users', userId, 'meta', 'onboarding')),
        getDoc(doc(db, 'users', userId, 'meta', 'preferences')),
        getDocs(collection(db, 'users', userId, 'ratings')),
      ]);
      if (metaSnap.exists() && metaSnap.data().complete === true) {
        set({ onboardingComplete: true });
      } else if (!metaSnap.exists() && ratingsSnap.size > 0) {
        // Legacy user: has ratings but pre-dates the meta doc — treat as complete
        set({ onboardingComplete: true });
      }
      if (prefsSnap.exists()) {
        set({ userPreferences: prefsSnap.data() });
      }
    } catch (err) {
      console.error('Failed to check onboarding/preferences meta:', err);
    }
    set({ _onboardingChecked: true });

    const unsub = onSnapshot(
      collection(db, 'users', userId, 'ratings'),
      (snap) => {
        const fromFirestore = {};
        snap.forEach(d => { fromFirestore[d.id] = d.data(); });
        set({ userRatings: { ...fromFirestore, ...pendingWrites } });
        // onboardingComplete is intentionally NOT set here — it is resolved once
        // on login (above) so that ratings written during onboarding don't
        // prematurely exit the user from the flow.
      },
      (err) => console.error('Ratings listener error:', JSON.stringify(err), err)
    );

    set({ _unsubRatings: unsub });
  },

  stopRatingsListener: () => {
    const unsub = get()._unsubRatings;
    if (unsub) {
      unsub();
      set({ _unsubRatings: null });
    }
  },
}));

export default useStore;
